import {MonthlySpendingData} from "../../../services/api/insight-api"
import {Category} from "../../../domain/category"
import {ChartScopePeriod, ChartScopeSettings, isMonthChartRange, MonthChartRange} from "./chart-scope-settings/chart-scope-settings"
import {sum} from "../../../util/compute-utils"

export default class InsightDataHelper {
  private readonly monthIndexForMonth: Record<string, number>
  private readonly totalSpendingByCategory: Record<number, number>
  public readonly totalSpendingByMonth: Record<number, number>
  public readonly totalIncomeByMonth: Record<number, number>
  public readonly spendingByMonthByTopCategory: Record<number, Record<number, number>>
  private readonly totalCo2ByCategory: Record<number, number>
  public readonly totalCo2ByMonth: Record<number, number>
  public readonly co2ByMonthByTopCategory: Record<number, Record<number, number>>

  constructor(
    public monthlySpendingData: MonthlySpendingData,
    public applicableCategories: Category[]
  ) {
    this.monthIndexForMonth = this.getMonthIndexForMonth()

    this.totalSpendingByCategory = this.getTotalByCategory((categoryId) => this.monthlySpendingData.spendingByMonthByCategory[categoryId])
    this.totalSpendingByMonth = this.getTotalByMonth((categoryId, monthStart) => this.monthlySpendingData.spendingByMonthByCategory[categoryId][this.getIndexForMonthStart(monthStart)])
    this.totalIncomeByMonth = this.getTotalByMonth((categoryId, monthStart) => this.monthlySpendingData.spendingByMonthByCategory[categoryId][this.getIndexForMonthStart(monthStart)], true)
    this.spendingByMonthByTopCategory = this.getTotalByMonthByTopCategory((categoryId, monthStart) => this.monthlySpendingData.spendingByMonthByCategory[categoryId][this.getIndexForMonthStart(monthStart)])

    this.totalCo2ByCategory = this.getTotalByCategory((categoryId) => this.monthlySpendingData.co2ByMonthByCategory[categoryId])
    this.totalCo2ByMonth = this.getTotalByMonth(this.getCategoryMonthCo2.bind(this))
    this.co2ByMonthByTopCategory = this.getTotalByMonthByTopCategory(this.getCategoryMonthCo2.bind(this))
  }

  private getMonthIndexForMonth(): { [month: string]: number } {
    let monthIndexForMonth: { [month: string]: number } = {}
    for (let i = 0; i < this.monthlySpendingData.months.length; i++) {
      const month = this.monthlySpendingData.months[i]
      const monthStart = new Date(month.month)
      monthIndexForMonth[`${monthStart.getFullYear()}${monthStart.getMonth()}`] = month.index
    }
    return monthIndexForMonth
  }

  private getTotalByCategory(getMonthCategory: (categoryId: number) => Record<number, number>): Record<number, number> {
    let totalByCategory: Record<number, number> = {}
    this.applicableCategories.filter(c => c.parentId != null).forEach(c => {
      totalByCategory[c.id] = Object.values(getMonthCategory(c.id)).reduce(sum, 0)
    })
    this.applicableCategories.filter(c => c.parentId == null).forEach(c => {
      totalByCategory[c.id] = this.applicableCategories
        .filter(x => x.parentId === c.id)
        .map(x => totalByCategory[x.id])
        .reduce(sum, 0)
    })
    return totalByCategory
  }

  private getTotalByMonth(getMonthCategory: (categoryId: number, monthStart: Date) => number, forIncome: boolean = false): Record<number, number> {
    let totalByMonth: Record<number, number> = {}
    this.monthlySpendingData.months.forEach(m => {
      totalByMonth[m.index] = this.applicableCategories
        .filter(c => c.parentId != null && c.isIncome === forIncome)
        .map(c => getMonthCategory(c.id, new Date(m.month)))
        .reduce(sum, 0)
    })
    return totalByMonth
  }

  private getTotalByMonthByTopCategory(getCategoryMonthAmount: (categoryId: number, monthStart: Date) => number): Record<number, Record<number, number>> {
    let spendingByMonthByCategory: Record<number, Record<number, number>> = {}
    this.applicableCategories
      .filter(c => c.parentId == null) // only top categories
      .forEach(c => {
        let spendingByMonthForCategory: Record<number, number> = {}
        this.monthlySpendingData.months.forEach(m => {
          spendingByMonthForCategory[m.index] = this.applicableCategories
            .filter(x => x.parentId === c.id)
            .map(x => getCategoryMonthAmount(x.id, new Date(m.month)))
            .reduce(sum, 0)
        })
        spendingByMonthByCategory[c.id] = spendingByMonthForCategory
      })
    return spendingByMonthByCategory
  }

  public getDataPointsForSelectedScope(categories: Category[], chartScopeSettings: ChartScopeSettings): number[] {
    return this.getSharedDataPointsForSelectedScope(categories, chartScopeSettings, this.getCategoryMonthSpending.bind(this), this.getCategoryTotalSpending.bind(this))
  }

  public getCo2DataPointsForSelectedScope(categories: Category[], chartScopeSettings: ChartScopeSettings): number[] {
    return this.getSharedDataPointsForSelectedScope(categories, chartScopeSettings, this.getCategoryMonthCo2.bind(this), this.getCategoryTotalCo2.bind(this))
  }

  private getSharedDataPointsForSelectedScope(categories: Category[], chartScopeSettings: ChartScopeSettings, getCategoryMonth: (categoryId: number, monthStart: Date) => number, getCategoryTotal: (categoryId: number) => number): number[] {
    const categorySpending = (isMonthChartRange(chartScopeSettings.range))
      ? categories.map(c => getCategoryMonth(c.id, (chartScopeSettings.range as MonthChartRange).monthStart))
      : categories.map(c => getCategoryTotal(c.id))

    return this.adjustForPeriod(categorySpending, chartScopeSettings.period)
  }

  private adjustForPeriod(dataPoints: number[], period: ChartScopePeriod): number[] {
    switch (period) {
      case "month": return dataPoints.map(x => x / this.monthlySpendingData.months.length)
      case "year": return dataPoints.map(x => x / this.monthlySpendingData.months.length * 12)
    }
  }

  private getCategoryMonthSpending(categoryId: number, monthStart: Date) {
    const monthIndex = this.getIndexForMonthStart(monthStart)
    const isForTopCategory = this.applicableCategories.find(c => c.id === categoryId)?.parentId == null
    return isForTopCategory
      ? this.spendingByMonthByTopCategory[categoryId][monthIndex]
      : this.monthlySpendingData.spendingByMonthByCategory[categoryId][monthIndex]
  }

  private getCategoryTotalSpending(categoryId: number): number {
    return this.totalSpendingByCategory[categoryId]
  }

  private getCategoryMonthCo2(categoryId: number, monthStart: Date): number {
    const monthIndex = this.getIndexForMonthStart(monthStart)
    const isForTopCategory = this.applicableCategories.find(c => c.id === categoryId)?.parentId == null
    return isForTopCategory
      ? this.co2ByMonthByTopCategory[categoryId][monthIndex]
      : this.monthlySpendingData.co2ByMonthByCategory[categoryId][monthIndex]
  }

  private getCategoryTotalCo2(categoryId: number) : number{
    return this.totalCo2ByCategory[categoryId]
  }

  private getIndexForMonthStart(monthStart: Date) {
    return this.monthIndexForMonth[`${monthStart.getFullYear()}${monthStart.getMonth()}`]
  }

}